
import * as moment from 'moment';
import levenshtein from "js-levenshtein";
import Dictionary from '../dictionary';
import * as d3v4 from "d3v4";
import { downloadPng } from '@/utils/exportToPNG';
import html2canvas from 'html2canvas';

export function normalizeCaseAndAccents(str) {
  if (str) {
    return str.toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  }
  return str;
}

/* Global function to capitalize first letter of the word (e.g. for stat names) */
export function capitalizeFirstLetter(string) {
  if (string) {
    return string.replace(/^./, string[0].toUpperCase());
  }
  return string;
}



/* -----------------------
Received object League (must contains endSameYear attribute) + Year and returns League Name + Year in case league ends same year it started
and returns last 2 digits of the current year and next year in case league ends next year it started.

Example:

La Liga XX/YY
Brasileirao XXXX 
-------------------------- */
export function getLeagueAndYearName(league, year) {
  if (!league)
    return "";

  let leagueNameAndYear = "";
  //If League ends the same year it started, then take Season Name (Mexico Clausura 2020, Colombia Apertura 2020...)
  if (league.endSameYear) {
    leagueNameAndYear = league.label + " '" + Number(year) % 100;
  } else {
    //Take last 2 digits
    year = year % 100;
    leagueNameAndYear = league.label + " " + year + "/" + (year + 1);
  }

  return leagueNameAndYear;
}

export function formatCash(n) {
  if (n < 0) {
    n *= -1;
    if (n < 1e3) return -n;
    if (n >= 1e3 && n < 1e6) return -(n / 1e3).toFixed(1) + " K";
    if (n >= 1e6 && n < 1e9) return -(n / 1e6).toFixed(1) + " M";
    if (n >= 1e9 && n < 1e12) return -(n / 1e9).toFixed(1) + " B";
    if (n >= 1e12) return -(n / 1e12).toFixed(1) + " T";
  }
  if (n < 1e3) return n;
  if (n >= 1e3 && n < 1e6) return +(n / 1e3).toFixed(1) + " K";
  if (n >= 1e6 && n < 1e9) return +(n / 1e6).toFixed(1) + " M";
  if (n >= 1e9 && n < 1e12) return +(n / 1e9).toFixed(1) + " B";
  if (n >= 1e12) return +(n / 1e12).toFixed(1) + " T";
}
//Used for pdf reports cover
export function formatDateTime(date, locale) {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: '2-digit'
  }).format(date);
}

/*
This function format Day as you can see in All Games section of playerResponse
Date before last week show as -> Jul 13th
Date in the last 7 days show as -> Last Friday
*/
export function formatDate(date, language = "en", inputFormat = 'YYYY-MM-DD') {
  let dateFormated = moment(date, inputFormat).locale(language).calendar({
    lastDay: function () {
      return '[' + Dictionary[language]["general"]["yesterday"] + ']';
    },
    sameDay: function () {
      return '[' + Dictionary[language]["general"]["today"] + ']';
    },
    lastWeek: function () {
      return '[' + Dictionary[language]["general"]["last"] + ']' + ' dddd';
    },
    sameElse: function () {
      return 'MMM Do';
    }
  });
  return dateFormated.charAt(0).toUpperCase() + dateFormated.slice(1);
}

/* Return month of date with capital first letter.
Ex: date 01/06 return June/Junio..etc depends on language (en,es...)
*/
export function getMonthOfDate(date, language) {
  let month = moment(date).locale(language).format('MMMM');
  return month.charAt(0).toUpperCase() + month.slice(1);
}

export function isForward(position) {
  //[AMR, AML, FWR, FWL, FW]
  return [10, 11, 12, 13, 14].includes(position);
}
export function isMidfielder(position) {
  //[DMC, MC, MR, ML, AMC]
  return [5, 6, 7, 8, 9].includes(position);
}
export function isDefender(position) {
  //[DC, DC, DL, DMR, DML]
  return [2, 3, 4, 15, 16].includes(position);
}
export function isGoalkeeper(position) {
  return position === 1; //GK
}

export function getChipColorByPosition(fieldPositionId) {
  switch (fieldPositionId) {
    case 1: //GK
      return "orange";
    case 2: //DC
    case 3: //DR
    case 4: //DL
      return "blue darken-2";
    case 5: //DMC
    case 15: //DMR
    case 16: //DML
      return "light-blue";
    case 6: //MC
    case 7: //MR
    case 8: //ML
      return "success";
    case 9: //AMC
    case 10: //AMR
    case 11: //AML
      return "lime";
    case 12: //FWR
    case 13: //FWL
    case 14: //FW
      return "red";
    default:
      return "";
  };
}

export function getHexColorByPosition(fieldPositionId) {
  switch (fieldPositionId) {
    case 1: //GK
      return "#ff9800";
    case 2: //DC
    case 3: //DR
    case 4: //DL
      return "#487bb8";
    case 5: //DMC
    case 15: //DMR
    case 16: //DML
      return "#03a9f4";
    case 6: //MC
    case 7: //MR
    case 8: //ML
      return "#4caf50";
    case 9: //AMC
    case 10: //AMR
    case 11: //AML
      return "#cddc39";
    case 12: //FWR
    case 13: //FWL
    case 14: //FW
      return "#F44336";
    case 0: //Sub
    case 17: //Sub
      return "#e0e0e0";
    default:
      return "";
  };
}

export function getPositionCode(fieldPositionCode = 1, position) {
  const fieldPositions = {
    1: ["RC", "Sub", "GK", "DC", "DR", "DL", "DMC", "MC", "MR", "ML", "AMC", "AMR", "AML", "FWR", "FWL", "FW", "DMR", "DML"], //Default/Driblab
    2: ["RC", "Sub", "GK", "CB", "RB", "LB", "CDM", "CM", "RM", "LM", "CAM", "RW", "LW", "RF", "LF", "ST", "RWB", "LWB"], //FIFA
    3: ["TR", "Sup", "POR", "DFC", "LD", "LI", "MCD", "MC", "MD", "MI", "MCO", "ED", "EI", "SDD", "SDI", "DC", "CAD", "CAI"], //Spanish
    4: ["-1", "0", "1", "4", "2", "3", "6", "8", "7", "11", "10", "7", "11", "7", "11", "9", "2", "3"] //Numeric
  };

  //Return the corresponding code or an empty string if the position is not defined
  if (fieldPositions[fieldPositionCode] && fieldPositions[fieldPositionCode].length > position + 1) return fieldPositions[fieldPositionCode][position + 1];
  else return "";
}

export function getRatingTextByFormatId(ratingFormatId = 1, rating) {
  const ratings = {
    1: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10], // Numbers
    2: ['F', 'D', 'C', 'B', 'A'], // Letters A-F
  };

  if (!rating || !ratings[ratingFormatId]) return "";

  if (ratingFormatId === 1) {
    if (rating < 1 || rating > 10) return "";
    return rating.toString();
  };

  if (ratingFormatId === 2) {
    if (rating >= 9) return 'A';
    if (rating >= 7) return 'B';
    if (rating >= 5) return 'C';
    if (rating >= 3) return 'D';
    if (rating >= 1) return 'F';
    return "";
  };
};

export function getRatingPositionByFormatId(ratingFormatId, rating) {
  if (!rating) return 0;

  if (ratingFormatId === 1) {
    if (rating < 1 || rating > 10) return 0;
    return rating;
  }

  if (ratingFormatId === 2) {
    if (rating >= 9) return 5; // A
    if (rating >= 7) return 4; // B
    if (rating >= 5) return 3; // C
    if (rating >= 3) return 2; // D
    if (rating >= 1) return 1; // F
    return 0;
  }

  return 0;
};

export function getRatingColorByFormatId(ratingFormatId = 1, rating) {
  const position = getRatingPositionByFormatId(ratingFormatId, rating);
  if (!position) return undefined;

  const max = ratingFormatId === 2 ? 5 : 10;
  return colorScale(1, max, position, 90);
};

export function getFieldPositionIdByDefaultCode(positionCode) {
  const driblabPositionCodes = {
    "RC": -1, "Sub": 0, "GK": 1, "DC": 2, "DR": 3, "DL": 4, "DMC": 5, "MC": 6, "MR": 7, "ML": 8,
    "AMC": 9, "AMR": 10, "AML": 11, "FWR": 12, "FWL": 13, "FW": 14, "DMR": 15, "DML": 16
  };
  return driblabPositionCodes[positionCode] ? driblabPositionCodes[positionCode] : "";
}

export function getPositionGroup(position) {
  switch (position) {
    case 1: //GK
      return "goalkeepers";
    case 2: //DC
    case 3: //DR
    case 4: //DL
    case 15: //DMR
    case 16: //DML
      return "defenses";
    case 5: //DMC
    case 6: //MC
    case 7: //MR
    case 8: //ML
    case 9: //AMC
    case 10: //AMR
    case 11: //AML
      return "midfielders";
    case 12: //FWR
    case 13: //FWL
    case 14: //FW
      return "forwards";
    default:
      return "";
  };
}

export function getPositionGroupComplete(position) {
  switch (position) {
    case 1: //GK
      return "goalkeepers";
    case 2: //DC
      return "defenders";
    case 3: //DR
    case 4: //DL
      return "fullbacks";
    case 15: //DMR
    case 16: //DML
      return "wingbacks";
    case 5: //DMC
    case 6: //MC
    case 7: //MR
    case 8: //ML
      return "midfielders";
    case 9: //AMC
    case 10: //AMR
    case 11: //AML
    case 12: //FWR
    case 13: //FWL
      return "wingers";
    case 14: //FW
      return "forwards";
    default:
      return "";
  };
}

export function getShortPositionsByTemplate(positionId, isAttackingMidfielder = false) {
  switch (positionId) {
    case 1: //GoalKeeper
      return [1]; //GK
    case 2: //Defender
      return [2]; //DC
    case 3: //FullBack
      return [3, 4]; //DR, DL
    case 4: //Midfielder
      return [5, 6, 7, 8]; //DMC, MC, MR, ML
    case 5:
      if (isAttackingMidfielder) //AttackingMidfielder
        return [9]; //AMC
      else //Winger
        return [9, 10, 11, 12, 13]; //AMC, AMR, AML, FWR, FWL
    case 6: //Forward
      return [14]; //FW
    case 7: //WingBack
      return [15, 16]; //DMR, DML
    default:
      return [];
  }
}

export function userHasRole(user, role) {
  if (user && user.roles) {
    return user.roles.includes("ROLE_USER") && (
      user.roles.includes(role) ||
      user.roles.includes("ROLE_ADMIN")
    );
  }
  return false;
}

/* Used in Radar, DribL (Radar Description) and Game Flow */
export function round(value, decimals) {
  return Number(Math.round(value + 'e' + decimals) + 'e-' + decimals);
}

export function prepareDataSet(dataset) {
  let players = _.chain(dataset.players)
    .map((p) => {
      let revName = _.chain(p.name).split(' ').reverse().join(' ').value();
      p.searchValue = normalizeCaseAndAccents(
        `${p.tName} ${p.name} ${p.tName} ${revName} ${p.tName} ${p.cC}`
      );
      p.position = p.pos;
      p.playerId = p.id;
      p.mv = p.mv || 0;
      p.arr = p.arr, //Has at least 1 season with Arrigo Data
        p.pd = p.pd //Has at least 1 season with Physical Data
      return p;
    })
    .orderBy(["mv", "name"], ["desc", "asc"])
    .value();

  let teams = _.chain(dataset.teams)
    .map((t) => {
      t.searchValue = normalizeCaseAndAccents(
        `${t.lName} ${t.name} ${t.cC} ${t.lName} ${t.coachName}`
      );
      t.mv = t.mv || 0;
      return t;
    })
    .orderBy(["mv", "name"], ["desc", "asc"])
    .value();
  let games = _.chain(dataset.games)
    .map((g) => {
      g.searchValue = normalizeCaseAndAccents(
        `${g.lName} ${g.date} ${g.htName} ${g.atName} ${g.lCC}`
      );
      return g;
    })
    .orderBy(["simpleDate", "id"], ["desc", "desc"])
    .value();
  let leagues = _.chain(dataset.leagues)
    .map((l) => {
      const esIso = Dictionary["es"] && Dictionary["es"]["iso"] && l.cC ? Dictionary["es"]["iso"][l.cC.toUpperCase()] : null;
      const enIso = Dictionary["en"] && Dictionary["en"]["iso"] && l.cC ? Dictionary["en"]["iso"][l.cC.toUpperCase()] : null;
      l.searchValue = normalizeCaseAndAccents(
        `${l.name} ${l.cName} ${l.cC ? (esIso || l.cC) : l.cC} ${l.cC ? (enIso || l.cC) : l.cC}`
      );
      return l;
    })
    .orderBy(["rank", "name", "id"], ['asc', "asc", "asc"])
    .value();
  dataset = {
    players: players,
    teams: teams,
    games: games,
    leagues: leagues
  };
  return dataset;
}

export function calculateWeightFilterItem(item, queryText, itemType) {
  let distance = false;
  let querySplitted = _.split(queryText, " ");
  let querySplittedMinDistances = [0];
  let nameDistance;
  switch (itemType) {
    case "player":
      querySplittedMinDistances = _.map(querySplitted, (q) => {
        let nameSplitted = _.split(item.name, " ");
        let nameSplittedDistances = _.map(nameSplitted, (n) =>
          levenshtein(
            normalizeCaseAndAccents(n),
            q
          )
        );
        let teamSplitted = _.split(item.tName, " ");
        let teamSplittedDistances = _.map(teamSplitted, (t) =>
          levenshtein(
            normalizeCaseAndAccents(t),
            q
          )
        );
        let min = _.min([
          ...nameSplittedDistances,
          ...teamSplittedDistances,
        ]);
        return min > 0 ? min : -1;
      });
      let playerNameDistance = levenshtein(
        normalizeCaseAndAccents(item.name),
        queryText
      );
      if (playerNameDistance === 1) {
        playerNameDistance = item.mv * -40;
      } else if (playerNameDistance === 0) {
        playerNameDistance = item.mv * -60;
        playerNameDistance += item.mv === 0 && queryText.length > 6 && _.includes(queryText, " ") ? -999 * 50 : playerNameDistance;
      }
      let playerNameIncludeDistance = _.includes(
        normalizeCaseAndAccents(item.name),
        queryText
      )
        ? -queryText.length
        : 3;

      // //coincide una parte
      let playerNameSplittedIncludeDistance = _.chain(item.name).split(" ").filter(nameSegment => _.size(nameSegment) >= 5).reduce((accum, nameSegment, index) => {
        let mod = index === 0 ? item.mv * 0.05 : item.mv * 0.66;
        accum = _.chain(queryText).split(" ").filter(querySegment => _.size(querySegment) >= 5)
          .some(querySegment => _.includes(normalizeCaseAndAccents(nameSegment), querySegment)).value() ? accum - mod : accum + 3;
        return accum;
      }, 0).value()

      nameDistance = _.min([playerNameDistance, playerNameIncludeDistance + playerNameSplittedIncludeDistance]);
      break;
    case "team":
      querySplittedMinDistances = _.map(querySplitted, (q) => {
        let coachNameSplitted = _.split(item.coachName, " ");
        let coachNameSplittedDistances = _.map(coachNameSplitted, (n) =>
          levenshtein(
            normalizeCaseAndAccents(n),
            q
          )
        );
        let teamSplitted = _.split(item.name, " ");
        let teamSplittedDistances = _.map(teamSplitted, (t) =>
          levenshtein(
            normalizeCaseAndAccents(t),
            q
          )
        );
        let leagueSplitted = _.split(item.lName, " ");
        let leagueSplittedDistances = _.map(leagueSplitted, (l) =>
          levenshtein(
            normalizeCaseAndAccents(l),
            q
          )
        );
        let min = _.min([
          ...coachNameSplittedDistances,
          ...teamSplittedDistances,
          ...leagueSplittedDistances,
        ]);
        return min > 0 ? min : -1;
      });
      let teamNameDistance = levenshtein(
        normalizeCaseAndAccents(item.name),
        queryText
      );
      let teamNameIncludeDistance = _.includes(
        normalizeCaseAndAccents(item.name),
        queryText
      )
        ? -queryText.length
        : 3;
      nameDistance = _.min([teamNameDistance, teamNameIncludeDistance]);
      break;
    case "game":
      querySplittedMinDistances = _.map(querySplitted, (q) => {
        let homeTeamSplitted = _.split(item.htName, " ");
        let homeTeamSplittedDistances = _.map(homeTeamSplitted, (t) =>
          levenshtein(
            normalizeCaseAndAccents(t),
            q
          )
        );
        let awayTeamSplitted = _.split(item.atName, " ");
        let awayTeamSplittedDistances = _.map(awayTeamSplitted, (t) =>
          levenshtein(
            normalizeCaseAndAccents(t),
            q
          )
        );
        let leagueSplitted = _.split(item.lName, " ");
        let leagueSplittedDistances = _.map(leagueSplitted, (l) =>
          levenshtein(
            normalizeCaseAndAccents(l),
            q
          )
        );
        let min = _.min([
          ,
          ...homeTeamSplittedDistances,
          ...awayTeamSplittedDistances,
          ...leagueSplittedDistances,
        ]);
        return min > 0 ? min : -1;
      });
      let htTeamJoin = _.join(item.htName, ' ')
      let htTeamIncludeQuery = _.some(querySplitted, (q) => htTeamJoin.includes(q));
      let atTeamJoin = _.join(item.atName, ' ')
      let atTeamIncludeQuery = _.some(querySplitted, (q) => atTeamJoin.includes(q));
      let dateDistance = levenshtein(
        normalizeCaseAndAccents(item.date),
        queryText
      );
      let homeTeamDistance = levenshtein(
        normalizeCaseAndAccents(item.htName),
        queryText
      );
      let awayTeamDistance = levenshtein(
        normalizeCaseAndAccents(item.atName),
        queryText
      );
      nameDistance = _.some(
        [dateDistance, homeTeamDistance, awayTeamDistance],
        (d) => d === 0
      ) || (htTeamIncludeQuery && atTeamIncludeQuery)
        ? -5
        : _.min([dateDistance, homeTeamDistance, awayTeamDistance]);

      break;
    case "league":
      querySplittedMinDistances = _.map(querySplitted, (q) => {
        let searchValueSplitted = _.split(item.searchValue, " ");
        let searchValueSplittedDistances = _.map(searchValueSplitted, (n) =>
          levenshtein(
            normalizeCaseAndAccents(n),
            q
          )
        );
        let min = _.min([
          ...searchValueSplittedDistances,
        ]);
        return min > 0 ? min : -1;
      });
      let countryNameDistance = levenshtein(
        normalizeCaseAndAccents(item.cName),
        queryText
      );
      let countryNameIncludeDistance = _.includes(
        normalizeCaseAndAccents(item.cName),
        queryText
      )
        ? -queryText.length
        : 3;
      let leagueNameDistance = levenshtein(
        normalizeCaseAndAccents(item.name),
        queryText
      );
      let leagueNameIncludeDistance = _.includes(
        normalizeCaseAndAccents(item.name),
        queryText
      )
        ? -queryText.length
        : 3;
      nameDistance = _.min([countryNameDistance, countryNameIncludeDistance, leagueNameDistance, leagueNameIncludeDistance]);
      break;
  }
  if (nameDistance >= 0 && nameDistance < 2) {
    nameDistance = -2;
  } else if (nameDistance === 2) {
    nameDistance = -1;
  } else if (nameDistance > 2) {
    nameDistance = 0;
  }
  distance = _.sum([...querySplittedMinDistances, nameDistance]);
  return distance;
}

export function levenshteinDist(str1, str2) {
  const distance = str2.includes(str1) ? 0 : levenshtein(str1, str2);
  const maxLength = Math.max(str1.length, str2.length);
  const normalizedScore = (1 - distance / maxLength) * 10;
  return Math.max(normalizedScore, 0);
};

export function normalizeText(text) {
  // Eliminar acentos y diacríticos
  const normalizedText = text.normalize('NFKD').replace(/[^\x00-\x7F]/g, '');
  // Convertir a minúsculas y eliminar caracteres no alfanuméricos
  const cleanedText = normalizedText.replace(/[^a-zA-Z0-9\s]/g, '').toLowerCase();
  return cleanedText;
};

const getStringSimilarity = (str1, str2) => {
  const words = str1.split(' ');
  if (words.length > 2) {
    words.push(`${words[0]} ${words[1]}`, `${words[1]} ${words[2]}`);
  }
  let sim = levenshteinDist(normalizeText(str2), normalizeText(str1));
  for (const word of words) {
    const newSim = levenshteinDist(normalizeText(str2), normalizeText(word));
    sim = Math.max(sim, newSim);
  }
  return sim;
};

export function calculateWeightPlayer(player, text) {
  const playerName = player.name;
  const playerTeam = player.tName;
  const playerValue = player.mv;

  const nameSim = getStringSimilarity(playerName, text);
  const nameCombSim = nameSim + playerValue / 180000;
  const teamSim = getStringSimilarity(playerTeam, text);
  const teamCombSim = (teamSim + playerValue / 180000) * 0.9;
  return Math.max(nameCombSim, teamCombSim);
}

export function calculateWeightGame(game, text) {
  const homeName = game.htName;
  const awayName = game.atName;
  const leagueName = game.lName;
  const date = game.date;

  const homeSim = getStringSimilarity(homeName, text);
  const awaySim = getStringSimilarity(awayName, text);
  const leagueSim = getStringSimilarity(leagueName, text);
  const dateSim = getStringSimilarity(date, text);
  return Math.max(homeSim, awaySim,leagueSim,dateSim);
}

export function isWorthToWeight(player, text){
  const playerName = normalizeText(player.name);
  const playerTeam = normalizeText(player.tName);
  
  const nameInclude = playerName.includes(text);
  const teamInclude = playerTeam.includes(text);

  const worth = player.mv > 250
  
  const querySize = _.size(text);
  let partsInclude = false;
  if(querySize > 5){
    let parts = [text.substring(0,3),text.substring(querySize-3)];
    partsInclude = _.some(parts,(p) => playerName.includes(p) || playerTeam.includes(p));
  }
  return nameInclude || teamInclude || (partsInclude && worth);
}

export function isGameWorthToWeight(game, text){
  const homeName = normalizeText(game.htName);
  const awayName = normalizeText(game.atName);
  const leagueName = normalizeText(game.lName)
  const date = normalizeText(game.date);
  
  const homeInclude = homeName.includes(text);
  const awayInclude = awayName.includes(text);
  const leagueInclude = leagueName.includes(text);
  const dateInclude = date.includes(text);

  const oneYearAgo = moment().subtract(1,'years');
  const matchDate = moment(game.date,'DD/MM/YYYY')
  const worth = matchDate.isSameOrAfter(oneYearAgo);
  
  const querySize = _.size(text);
  let partsInclude = false;
  if(querySize > 5){
    let parts = [text.substring(0,3),text.substring(querySize-3)];
    partsInclude = _.some(parts,(p) => {
      return homeName.includes(p) || awayName.includes(p)
      || leagueName.includes(p) || date.includes(p)
    });
  }
  return homeInclude || awayInclude 
  || leagueInclude || dateInclude
  || (partsInclude && worth);
}

export function getNeutralColors(side) {
  return side === 'home' ? ["#6d7de5", "#3366FF"] : ["#757575", "#02081A"]; //[lightSecondary, secondary], [primary, brandDark]
}

export function getDataUrl(img) {
  // Create canvas
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  // Set width and height
  canvas.width = img.width;
  canvas.height = img.height;
  // Draw the image
  ctx.drawImage(img, 0, 0);
  return canvas.toDataURL('image/png');
}

export function getAPI_URL() {
  const NODE_ENV = process.env.NODE_ENV;
  const PROTOCOL = process.env.PROTOCOL || 'http://';
  const END_POINT_URL = process.env.END_POINT_URL || window.location.hostname;
  const END_POINT_PORT = process.env.END_POINT_PORT || '8080';

  const API_URL = NODE_ENV === 'production' ? PROTOCOL + END_POINT_URL + '/' : PROTOCOL + END_POINT_URL + ':' + END_POINT_PORT + '/';
  return API_URL;
}


export function getCurrencies() {
  return [
    {
      name: "€ (EUR)",
      code: "EUR",
      symbol: "€",
      countryCode: "eu",
      currencyId: 1
    },
    {
      name: "$ (USD)",
      code: "USD",
      symbol: "$",
      countryCode: "us",
      currencyId: 2
    },
    {
      name: "¥ (CNY)",
      code: "CNY",
      symbol: "¥",
      countryCode: "cn",
      currencyId: 3
    },
    {
      name: "£ (GBP)",
      code: "GBP",
      symbol: "£",
      countryCode: "gb",
      currencyId: 4
    },
    {
      name: "¥ (JPY)",
      code: "JPY",
      symbol: "¥",
      countryCode: "jp",
      currencyId: 5
    },
    {
      name: "$ (MXN)",
      code: "MXN",
      symbol: "$",
      countryCode: "mx",
      currencyId: 6
    },
    {
      name: "₽ (RUB)",
      code: "RUB",
      symbol: "₽",
      countryCode: "ru",
      currencyId: 7
    },
    {
      name: "$ (AUD)",
      code: "AUD",
      symbol: "$",
      countryCode: "au",
      currencyId: 8
    },
    {
      name: "R$ (BRL)",
      code: "BRL",
      symbol: "R$",
      countryCode: "br",
      currencyId: 9
    }
  ]
}

export function isWhite(color) {
  return color === "#FFF" || color === "#fff" || color === "#FFFFFF" || color === "#ffffff" || color === "white";
}

export function pickHex(color1, color2, weight) {
  var p = weight;
  var w = p * 2 - 1;
  var w1 = (w / 1 + 1) / 2;
  var w2 = 1 - w1;
  var rgb = [Math.round(color1[0] * w1 + color2[0] * w2),
  Math.round(color1[1] * w1 + color2[1] * w2),
  Math.round(color1[2] * w1 + color2[2] * w2)];
  return rgb;
}

export function colorScale(minDomain, maxDomain, value, steps, withOpacity = false) {

  if (value < minDomain)
    value = minDomain;

  //Range of colors in HSV format gradient
  let colorsArray = ["#fb0e0e", "#fb130e", "#fb180f", "#fa1d0f", "#fa220f", "#fa270f", "#fa2c10", "#fa3110", "#f93610", "#f93b11", "#f94011", "#f94511", "#f84911", "#f84e12", "#f85312",
    "#f85812", "#f85d13", "#f76113", "#f76613", "#f76b13", "#f76f14", "#f77414", "#f67914", "#f67d14", "#f68215", "#f68715", "#f58b15", "#f59016", "#f59416", "#f59916", "#f59d16", "#f4a217",
    "#f4a617", "#f4aa17", "#f4af17", "#f4b318", "#f3b818", "#f3bc18", "#f3c019", "#f3c519", "#f3c919", "#f2cd19", "#f2d11a", "#f2d61a", "#f2da1a", "#f1de1a", "#f1e21b", "#f1e61b", "#f1ea1b",
    "#f1ee1b", "#eef01c", "#eaf01c", "#e5f01c", "#e1f01d", "#dcf01d", "#d8ef1d", "#d4ef1d", "#cfef1e", "#cbef1e", "#c6ee1e", "#c2ee1e", "#beee1f", "#baee1f", "#b5ee1f", "#b1ed1f", "#aded20",
    "#a9ed20", "#a4ed20", "#a0ed20", "#9cec21", "#98ec21", "#94ec21", "#90ec21", "#8cec22", "#88eb22", "#84eb22", "#80eb22", "#7ceb23", "#78ea23", "#74ea23", "#70ea23", "#6cea24", "#68ea24",
    "#64e924", "#60e924", "#5ce925", "#58e925", "#54e925", "#51e825", "#4de826", "#49e826", "#45e826", "#42e726", "#3ee727", "#3ae727", "#37e727", "#33e727", "#2fe628", "#2ce628", "#28e628"];
  
  /*let colorsArray = ["#df5555", "#df5754", "#e05953", "#e05b52", "#e15d51",
  "#e15f50", "#e2614f", "#e2634e", "#e3654d", "#e3674c","#e4694b", "#e46b4a", 
  "#e56d49", "#e56f48", "#e67147","#e67346", "#e77545", "#e77744", "#e87943", "#e87b42",
  "#e97d41", "#e97f40", "#ea813f", "#ea833e", "#eb853d",
  "#eb873c", "#ec893b", "#ec8b3a", "#ed8d39", "#ed8f38",
  "#ee9137", "#ee9336","#f4a617", "#f4aa17", "#f4af17", "#f4b318", "#f3b818", "#f3bc18", 
  "#f3c019", "#f3c519", "#f3c919", "#f2cd19", "#f2d11a", "#f2d61a", "#f2da1a", "#f1de1a", 
  "#f1e21b", "#f1e61b", "#f1ea1b","#f1ee1b", "#eef01c", "#eaf01c", "#e5f01c", "#e1f01d", 
  "#dcf01d", "#d8ef1d", "#d4ef1d", "#cfef1e", "#cbef1e", "#c6ee1e", "#c2ee1e", "#beee1f", 
  "#baee1f", "#b5ee1f", "#b1ed1f", "#aded20","#a9ed20", "#a4ed20", "#a0ed20", "#9cec21", 
  "#98ec21", "#94ec21", "#90ec21", "#8cec22", "#88eb22", "#84eb22", "#80eb22", "#7ceb23", 
  "#78ea23", "#74ea23", "#70ea23", "#6cea24", "#68ea24","#64e924", "#60e924", "#5ce925", 
  "#58e925", "#54e925", "#51e825", "#4de826", "#49e826", "#45e826", "#42e726", "#3ee727", 
  "#3ae727", "#37e727", "#33e727", "#2fe628", "#2ce628", "#28e628"];*/


  //Get scale functions.
  // - Domain: Input values accepted
  // - Range: Output values returned (in this case between 0 - steps-1)
  let colorScale = d3v4.scaleLinear().domain([minDomain, maxDomain]).range([0, steps - 1]);

  //Get index of colorsArray
  let index = colorScale(value);
  //Get whole part of the index
  let color = colorsArray[Math.trunc(index)];

  // Scores less than 50: reduce intensity
  if (value < 50 && withOpacity) {
    const opacityScale = d3v4.scaleLinear()
      .domain([minDomain, 50])
      .range([0.35, 1])
      .clamp(true);

    const rgb = d3v4.rgb(color);

    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacityScale(value)})`;
  }

  return color;
}

export function adjustPositionToOrlegi(position) {
  switch (position) {
    case 1: //GK
      return "GK";
    case 2: //DC
      return "DC";
    case 3: //DR
    case 4: //DL
    case 16: //DML
    case 15: //DMR
      return "DR";
    case 5: //DMC
    case 6: //MC
    case 8: //ML
    case 7: //MR
      return "MC";
    case 14: //FW
      return "FW";
    case 9: //AMC
      return "AMC";
    case 10: //AMR
    case 11: //AML
    case 12: //FWR
    case 13: //FWL
      return "FWR";
  }
  return null;
}

export function footIdToName(language, footId) {
  switch (position) {
    case 8: return `${Dictionary[language]["general"]["left"]}`
    case 9: return `${Dictionary[language]["general"]["both"]}`
    case 10: return `${Dictionary[language]["general"]["right"]}`
    default:
      return "";
  }
  return null;
}

export function formatNumber(n) {
  if (Math.abs(n) < 1e3) return n;
  if (Math.abs(n) >= 1e3 && Math.abs(n) < 1e6) return + round((n / 1e3), 1) + " K";
  if (Math.abs(n) >= 1e6 && Math.abs(n) < 1e9) return + round((n / 1e6), 1) + " M";
  if (Math.abs(n) >= 1e9 && Math.abs(n) < 1e12) return + round((n / 1e9), 1) + " B";
  if (Math.abs(n) >= 1e12) return + round((n / 1e12), 1) + " T";
}

export function groupLeaguesByAreas(leagues) {
  const orderLeaguesByType = (key) => {
    switch (key) {
      case 'Continental':
        return 0;
      case '1':
      case '2':
      case '3':
      case '4':
      case '5':
      case '6':
        return 1;
      case 'Country Cup':
        return 2;
      case 'Other':
        return 3;
      default:
        return 4;
    }
  };
  //3 National Team, 9 National Friendly, 10 National Qualifier
  //2 Continental, 4 Country Cup
  let confederations = {
    UEFA: _.chain(leagues)
      .filter(l => l.isMale && ([0, 1, 2].includes(l.area) || l.continentId === 0) && ![3, 9, 10].includes(l.leagueTypeId) && !l.isYouth)
      .groupBy((l) => {
        if (l.leagueTypeId === 2 || l.leagueTypeId === 4) {
          return l.leagueType;
        } else if (l.leagueTypeId === 1) {
          return l.division;
        } else {
          return 'Other';
        }
      })
      .map((val, key) => {
        return {
          order: orderLeaguesByType(key),
          title: key,
          children: _.orderBy(
            val,
            ["isHired", "division", "ranking", "label"],
            ["desc", "asc", "asc", "asc"]
          )
        };
      })
      .orderBy("order", "asc")
      .value(),
    CONCACAF: _.chain(leagues)
      .filter(l => l.isMale && (l.area === 5 || l.continentId === 2) && ![3, 9, 10].includes(l.leagueTypeId) && !l.isYouth)
      .groupBy((l) => {
        if (l.label == 'Liga MX A U21')
          console.log("", l);
        if (l.leagueTypeId === 2 || l.leagueTypeId === 4) {
          return l.leagueType;
        } else if (l.leagueTypeId === 1) {
          return l.division;
        } else {
          return 'Other';
        }
      })
      .map((val, key) => {
        return {
          order: orderLeaguesByType(key),
          title: key,
          children: _.orderBy(
            val,
            ["isHired", "division", "ranking", "label"],
            ["desc", "asc", "asc", "asc"]
          )
        };
      })
      .orderBy("order", "asc")
      .value(),
    CONMEBOL: _.chain(leagues)
      .filter(l => l.isMale && ([3, 4].includes(l.area) || l.continentId === 1) && ![3, 9, 10].includes(l.leagueTypeId) && !l.isYouth)
      .groupBy((l) => {
        if (l.leagueTypeId === 2 || l.leagueTypeId === 4) {
          return l.leagueType;
        } else if (l.leagueTypeId === 1) {
          return l.division;
        } else {
          return 'Other';
        }
      })
      .map((val, key) => {
        return {
          order: orderLeaguesByType(key),
          title: key,
          children: _.orderBy(
            val,
            ["isHired", "division", "ranking", "label"],
            ["desc", "asc", "asc", "asc"]
          )
        };
      })
      .orderBy("order", "asc")
      .value(),
    CAF: _.chain(leagues)
      .filter(l => l.isMale && (l.area === 7 || l.continentId === 4) && ![3, 9, 10].includes(l.leagueTypeId) && !l.isYouth)
      .groupBy((l) => {
        if (l.leagueTypeId === 2 || l.leagueTypeId === 4) {
          return l.leagueType;
        } else if (l.leagueTypeId === 1) {
          return l.division;
        } else {
          return 'Other';
        }
      })
      .map((val, key) => {
        return {
          order: orderLeaguesByType(key),
          title: key,
          children: _.orderBy(
            val,
            ["isHired", "division", "ranking", "label"],
            ["desc", "asc", "asc", "asc"]
          )
        };
      })
      .orderBy("order", "asc")
      .value(),
    AFC: _.chain(leagues)
      .filter(l => l.isMale && (l.area === 6 || l.continentId === 3) && ![3, 9, 10].includes(l.leagueTypeId) && !l.isYouth)
      .groupBy((l) => {
        if (l.leagueTypeId === 2 || l.leagueTypeId === 4) {
          return l.leagueType;
        } else if (l.leagueTypeId === 1) {
          return l.division;
        } else {
          return 'Other';
        }
      })
      .map((val, key) => {
        let newVal = val;
        //Look for duplicates leagueIds
        const lookup = val.reduce((a, e) => {
          a[e.id] = ++a[e.id] || 0;
          return a;
        }, {});
        //Sort desc seasonStartDate
        let duplicates = val.filter(e => lookup[e.id]).sort((a, b) => a.seasonStartDate - b.seasonStartDate);
        if (duplicates.length) {
          //Get non duplicate leagues
          newVal = val.filter(l => l.id !== duplicates[0].id);
          //Add newer season 
          newVal.splice(val.findIndex(l => l.id === duplicates[0].id), 0, duplicates[0]);
        }

        return {
          order: orderLeaguesByType(key),
          title: key,
          children: _.orderBy(
            newVal,
            ["isHired", "division", "ranking", "label"],
            ["desc", "asc", "asc", "asc"]
          )
        };
      })
      .orderBy("order", "asc")
      .value()
  };
  confederations = _.chain(confederations)
    .map((val, key) => {
      return {
        title: key,
        children: val
      };
    })
    .orderBy("title", "desc")
    .value();
  return confederations;
}

export function measureText(string, fontSize = 10) {
  const widths = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0.278125, 0.278125, 0.35625, 0.55625,
    0.55625, 0.890625, 0.6671875, 0.1921875, 0.334375, 0.334375, 0.390625, 0.584375, 0.278125, 0.334375, 0.278125, 0.303125, 0.55625,
    0.55625, 0.55625, 0.55625, 0.55625, 0.55625, 0.55625, 0.55625, 0.55625, 0.55625, 0.278125, 0.278125, 0.5859375, 0.584375, 0.5859375,
    0.55625, 1.015625, 0.6671875, 0.6671875, 0.7234375, 0.7234375, 0.6671875, 0.6109375, 0.778125, 0.7234375, 0.278125, 0.5, 0.6671875,
    0.55625, 0.834375, 0.7234375, 0.778125, 0.6671875, 0.778125, 0.7234375, 0.6671875, 0.6109375, 0.7234375, 0.6671875, 0.9453125,
    0.6671875, 0.6671875, 0.6109375, 0.278125, 0.35625, 0.278125, 0.478125, 0.55625, 0.334375, 0.55625, 0.55625, 0.5, 0.55625,
    0.55625, 0.278125, 0.55625, 0.55625, 0.2234375, 0.2421875, 0.5, 0.2234375, 0.834375, 0.55625, 0.55625, 0.55625, 0.55625,
    0.334375, 0.5, 0.278125, 0.55625, 0.5, 0.7234375, 0.5, 0.5, 0.5, 0.35625, 0.2609375, 0.3546875, 0.590625];
  const avg = 0.5293256578947368;

  return string
    .split('')
    .map(c => c.charCodeAt(0) < widths.length ? widths[c.charCodeAt(0)] : avg)
    .reduce((cur, acc) => acc + cur) * fontSize;
}

export function hexToRgb(hex) {
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : null;
}

export function deltaE(rgbA, rgbB) {
  let labA = rgb2lab(rgbA);
  let labB = rgb2lab(rgbB);
  let deltaL = labA[0] - labB[0];
  let deltaA = labA[1] - labB[1];
  let deltaB = labA[2] - labB[2];
  let c1 = Math.sqrt(labA[1] * labA[1] + labA[2] * labA[2]);
  let c2 = Math.sqrt(labB[1] * labB[1] + labB[2] * labB[2]);
  let deltaC = c1 - c2;
  let deltaH = deltaA * deltaA + deltaB * deltaB - deltaC * deltaC;
  deltaH = deltaH < 0 ? 0 : Math.sqrt(deltaH);
  let sc = 1.0 + 0.045 * c1;
  let sh = 1.0 + 0.015 * c1;
  let deltaLKlsl = deltaL / (1.0);
  let deltaCkcsc = deltaC / (sc);
  let deltaHkhsh = deltaH / (sh);
  let i = deltaLKlsl * deltaLKlsl + deltaCkcsc * deltaCkcsc + deltaHkhsh * deltaHkhsh;
  return i < 0 ? 0 : Math.sqrt(i);
}

export function rgb2lab(rgb) {
  let r = rgb[0] / 255, g = rgb[1] / 255, b = rgb[2] / 255, x, y, z;
  r = (r > 0.04045) ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
  g = (g > 0.04045) ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92;
  b = (b > 0.04045) ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92;
  x = (r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047;
  y = (r * 0.2126 + g * 0.7152 + b * 0.0722) / 1.00000;
  z = (r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883;
  x = (x > 0.008856) ? Math.pow(x, 1 / 3) : (7.787 * x) + 16 / 116;
  y = (y > 0.008856) ? Math.pow(y, 1 / 3) : (7.787 * y) + 16 / 116;
  z = (z > 0.008856) ? Math.pow(z, 1 / 3) : (7.787 * z) + 16 / 116;
  return [(116 * y) - 16, 500 * (x - y), 200 * (y - z)]
}

export function getDocumentIconById(id) {
  switch (id) {
    case 1:
      return "center_focus_strong";
    case 2:
      return "insert_chart_outlined";
    case 3:
      return "airline_seat_flat";
    case 4:
      return "directions_run";
    case 5:
      return "theater_comedy";
    case 6:
      return "emoji_objects";
    case 7:
      return "note";
    case 9:
      return "rate_review";
    case 10:
      return "accessibility";
    default:
      return "emoji_objects";
  };
}

export function getColorByFileName(name) {
  const extension = _.chain(name).split(".").last().value();
  switch (extension) {
    case "svg":
    case "png":
    case "jpeg":
    case "jpg":
    case "gif":
      return "orange";
    case "doc":
    case "docx":
    case "odt":
      return "blue";
    case "pdf":
      return "red";
    case "ods":
    case "xls":
    case "xlsm":
    case "xlsx":
      return "green";
    default:
      return "";
  };
}

export function getPercentageColor(percentage) {
  const percentageNum = +percentage;
  if (percentageNum < 60) return 'red';
  if (percentageNum < 80) return 'orange';
  if (percentageNum < 90) return 'yellow darken-1';
  return 'green';
}

/* Hierarchy of supervised items */
export function getSupervisedItems(supervisedItems, userType, ownerId, clientId = null){
  let userTypeIdsAllowed = [];
  let isDallas = clientId && clientId === 10400;
  //For Dallas (10400) allow supervised between same level
  switch (userType){
    case "Ownership": //1
      userTypeIdsAllowed = [2, 9, 7, 8];
      if(isDallas)
        userTypeIdsAllowed = userTypeIdsAllowed.concat(1);
      break;
    case "SportingDirector": //2
    case "Coach": //9
      userTypeIdsAllowed = [7, 8];
      if(isDallas)
        userTypeIdsAllowed = userTypeIdsAllowed.concat(2,9);
      break;
    default:
      break;
  }
  return supervisedItems.filter(si => userTypeIdsAllowed.includes(si.userTypeId) || si.createdByUserId == ownerId); //createdById
}


export function getAlertTypeTitle(alert, language = "en") {
  switch (alert.alertTypeId) {
    case 12: // Contract alert
      return alert.playerName || Dictionary[language]["inbox"]["new_contract_alert"];
    case 10: // Injury alert
      return alert.playerName || Dictionary[language]["inbox"]["new_injury_alert"];
    case 9: // Team alert
      return alert.playerName || Dictionary[language]["inbox"]["new_team_alert"];
    case 8: // Market value alert
      return alert.playerName || Dictionary[language]["inbox"]["new_marketValue_alert"];
    case 11: // Game alert
      return alert.playerName || Dictionary[language]["inbox"]["new_game_alert"];
    case 14: // New or updated scouting calendar alert
      const alertText = alert.createdAt === alert.updatedAt ? Dictionary[language]["alerts"]["newEventCalendar"] : Dictionary[language]["alerts"]["updatedEventCalendar"];
      return alert.taskName || alertText;
    case 15: // Share alert
      return alert.playerName || alert.teamName || alert.gameName || alert.playlistName || alert.alertComment;
    case 13: // New player alert
      return alert.searchName;
    default:
      break;
  }
}

export function getAlertTypeText(alert, language = "en", currencySymbol = '€') {
  switch (alert.alertTypeId) {
    case 12: // Contract alert
      return alert.contract ? `${Dictionary[language]["inbox"]["new_contract_alert"]}: ${alert.contract}` : Dictionary[language]["inbox"]["new_contract_alert"];
    case 10: // Injury alert
      return alert.injury ? `${Dictionary[language]["inbox"]["new_injury_alert"]}: ${alert.injury}` : Dictionary[language]["inbox"]["new_injury_alert"];
    case 9: // Team alert
      return alert.teamName ? `${Dictionary[language]["inbox"]["new_team_alert"]}: ${alert.teamName}` : Dictionary[language]["inbox"]["new_team_alert"];
    case 8: // Market value alert
      return alert.marketValue ? `${Dictionary[language]["inbox"]["new_marketValue_alert"]}: ${currencySymbol} ${formatCash(alert.marketValue * 1000)}` : Dictionary[language]["inbox"]["new_marketValue_alert"];
    case 11: // Game alert
      return alert.gameName ? `${Dictionary[language]["inbox"]["new_game_alert"]}: ${alert.gameName}` : Dictionary[language]["inbox"]["new_game_alert"];
    case 14: // New or updated scouting calendar alert
      const alertText = alert.createdAt === alert.updatedAt ? Dictionary[language]["alerts"]["newEventCalendar"] : Dictionary[language]["alerts"]["updatedEventCalendar"];
      return `Scouting Calendar: ${alertText}`;
    case 15: // Share alert
      // In turkish, username goes first
      return language === 'tr' ? `${alert.createdByUsername} ${Dictionary[language]["alerts"]["newShareFrom"]}`
        : `${Dictionary[language]["alerts"]["newShareFrom"]} ${alert.createdByUsername}`;
    case 13: // New player alert
      return alert.alertComment ? `${Dictionary[language]["videoUpload"]["newPlayer"]}: ${alert.alertComment}` : Dictionary[language]["videoUpload"]["newPlayer"];
    default:
      break;
  }
}

export function getAlertLink(alert) {
  switch (alert.alertTypeId) {
    case 12: // Contract alert
      return '/player-alert?type=5';
    case 10: // Injury alert
      return '/player-alert?type=4';
    case 9: // Team alert
      return '/player-alert?type=2';
    case 8: // Market value alert
      return '/player-alert?type=3';
    case 11: // Game alert
      return '/player-alert?type=1';
    case 14: // New or updated scouting calendar alert
      return `/scouting-calendar?id=${alert.targetId}`;
    case 15: // Share alert
      return '/shares?tab=2';
    case 13: // New player alert
      return `/player-scout/${alert.targetId}`;
    default:
      return '/alerts';
  }
}

export function getAlertIcon(alert) {
  switch (alert.alertTypeId) {
    case 8: // Market value alert
      return "attach_money"
    case 9: // Team alert
      return "shield"
    case 10: // Injury alert
      return "local_hospital"
    case 11: // Game alert
      return "games"
    case 12: // Contract alert
      return "border_color"
    case 13:
      return "center_focus_strong";
    case 14: // Event
      return "event";
    case 15: // Share
      return "share";
    default:
      return "access_alarm"
  }
}

export function shotCoordToAreaName(s) {
  if (s.x >= 94.2 && s.y >= 36.8 && s.y <= 63.2) return 'goalArea';
  if (s.x >= 83 && s.y >= 21.1 && s.y < 36.8) return 'boxRight';
  if (s.x >= 83 && s.x < 94.2 && s.y >= 36.8 && s.y <= 63.2) return 'boxCenter';
  if (s.x >= 83 && s.y > 63.2 && s.y <= 78.9) return 'boxLeft';
  if (s.x >= 83 && s.y < 21.1) return 'outBoxDeepRight';
  if (s.x >= 83 && s.y > 78.9) return 'outBoxDeepLeft';
  if (s.x >= 65 && s.x < 83 && s.y < 21.1) return 'outBoxRight';
  if (s.x >= 65 && s.x < 83 && s.y >= 21.1 && s.y <= 35.5) return 'outBoxCentreRight';
  if (s.x >= 65 && s.x < 83 && s.y > 35.5 && s.y <= 64.4) return 'outBoxCentre';
  if (s.x >= 65 && s.x < 83 && s.y > 64.4 && s.y <= 78.9) return 'outBoxCentreLeft';
  if (s.x >= 65 && s.x < 83 && s.y > 78.9) return 'outBoxLeft';
  if (s.x >= 50 && s.x < 65 && s.y < 21.1) return 'right35';
  if (s.x >= 50 && s.x < 65 && s.y >= 21.1 && s.y <= 35.5) return 'centreRight35';
  if (s.x >= 50 && s.x < 65 && s.y > 35.5 && s.y <= 64.4) return 'centre35';
  if (s.x >= 50 && s.x < 65 && s.y > 64.4 && s.y <= 78.9) return 'centreLeft35';
  if (s.x >= 50 && s.x < 65 && s.y > 78.9) return 'left35';
}


export function getProductInfoById(id, language = "en") {
  //Based on dim_product table
  switch (id) {
    case 1: //driblab xy
      return { id: id, name: Dictionary[language]["roles"]["ROLE_EVENTING_DATA"], description: Dictionary[language]["roles"]["ROLE_EVENTING_DATA_description"], icon: "tune" };
    case 2: // physical
      return { id: id, name: Dictionary[language]["roles"]["ROLE_PHYSICAL_DATA"], description: Dictionary[language]["roles"]["ROLE_PHYSICAL_DATA_description"], icon: "speed" };
    case 3: // arrigo
      return { id: id, name: Dictionary[language]["roles"]["ROLE_ARRIGO"], description: Dictionary[language]["roles"]["ROLE_ARRIGO_description"], icon: "fa-wand-magic-sparkles" };
    case 4: // tracking
      return { id: id, name: Dictionary[language]["roles"]["ROLE_TRACKING"], description: Dictionary[language]["roles"]["ROLE_TRACKING_description"], icon: "timeline" };
    case 5: //capture
      return { id: id, name: Dictionary[language]["roles"]["ROLE_VIDEO"], description: Dictionary[language]["roles"]["ROLE_VIDEO_description"], icon: "videocam" };
    case 6: // hub
      return { id: id, name: Dictionary[language]["roles"]["ROLE_DRIBLAB_HUB"], description: Dictionary[language]["roles"]["ROLE_DRIBLAB_HUB_description"], icon: "device_hub" };
    case 7: // api
      return { id: id, name: Dictionary[language]["roles"]["ROLE_API"], description: Dictionary[language]["roles"]["ROLE_API_description"], icon: "account_tree" };
    case 8: // consultancy
      return { id: id, name: Dictionary[language]["roles"]["ROLE_REPORTS"], description: Dictionary[language]["roles"]["ROLE_REPORTS_description"], icon: "assignment" };
    default:
      return { id: id, name: "", description: "", icon: "" };
  }
}

export function getProductInfoByRoleId(id, language = "en") {
  //Based on dim_role table
  switch (id) {
    case 32: //driblab xy
      return { roleId: id, productId: 1, name: Dictionary[language]["roles"]["ROLE_EVENTING_DATA"], description: Dictionary[language]["roles"]["ROLE_EVENTING_DATA_description"], icon: "tune" };
    case 29: // physical
      return { roleId: id, productId: 2, name: Dictionary[language]["roles"]["ROLE_PHYSICAL_DATA"], description: Dictionary[language]["roles"]["ROLE_PHYSICAL_DATA_description"], icon: "speed" };
    case 35: // arrigo
      return { roleId: id, productId: 3, name: Dictionary[language]["roles"]["ROLE_ARRIGO"], description: Dictionary[language]["roles"]["ROLE_ARRIGO_description"], icon: "fa-wand-magic-sparkles" };
    case 34: // tracking
      return { roleId: id, productId: 4, name: Dictionary[language]["roles"]["ROLE_TRACKING"], description: Dictionary[language]["roles"]["ROLE_TRACKING_description"], icon: "timeline" };
    case 31: //capture
      return { roleId: id, productId: 5, name: Dictionary[language]["roles"]["ROLE_VIDEO"], description: Dictionary[language]["roles"]["ROLE_VIDEO_description"], icon: "videocam" };
    case 33: // hub
      return { roleId: id, productId: 6, name: Dictionary[language]["roles"]["ROLE_DRIBLAB_HUB"], description: Dictionary[language]["roles"]["ROLE_DRIBLAB_HUB_description"], icon: "device_hub" };
    case 23: // api
      return { roleId: id, productId: 7, name: Dictionary[language]["roles"]["ROLE_API"], description: Dictionary[language]["roles"]["ROLE_API_description"], icon: "account_tree" };
    case 21: // consultancy
      return { roleId: id, productId: 8, name: Dictionary[language]["roles"]["ROLE_REPORTS"], description: Dictionary[language]["roles"]["ROLE_REPORTS_description"], icon: "assignment" };
    default:
      return { roleId: id, productId: null, name: "", description: "", icon: "" };
  }
}

export function getRadarTemplateSubtitle(groupKey, language = "en") {
  const productsInfo = {
    ROLE_PHYSICAL_DATA: { icon: 'speed', label: Dictionary[language]['roles']['ROLE_PHYSICAL_DATA'], size: '16px' },
    ROLE_EVENTING_DATA: { icon: 'tune', label: Dictionary[language]['roles']['ROLE_EVENTING_DATA'], size: '16px' },
    ROLE_ARRIGO: { icon: 'fa-wand-magic-sparkles', label: Dictionary[language]['roles']['ROLE_ARRIGO'], size: '12px' },
  };
  return productsInfo[groupKey] || { icon: '', label: Dictionary[language]['gbe']['other'], size: '' };
}
//Receive seconds and return time in format mm:ss
export function getTime(valueInSeconds) {
  return new Date(valueInSeconds * 1000).toISOString().substring(14, 19);
}

export function getRadioStyle(template, isDisabled) {
  if (isDisabled) return { '--unselected-radio-color': "#BDBDBD" }; // no custom color, fall back to disabled gray

  const positionColorsHex = {
    1: "#FF9800", // orange
    2: "#1976D2", // blue darken-2
    3: "#1976D2", // blue darken-2
    4: "#03A9F4", // light-blue
    5: "#CDDC39", // lime
    6: "#F44336", // red
    7: "#03A9F4", // light-blue
  };
  return { '--unselected-radio-color': positionColorsHex[template.positionId] };
}

export function getDistanceBetweenPoints(x1, x2, y1, y2) {
  return Math.sqrt((x1 - x2) * (x1 - x2) + (y1 - y2) * (y1 - y2));
}

/**
 * Function that display a given value with the corresponding distance unit.
 * 
 * @param {*} n the given value.
 * @param {*} units the distance unit corresponding to the given value.
 * 
 * @returns the metric with its proper unit.
 */
export function getDistanceInRightUnits(n, units) {
  if (n === null || n === undefined || n === "" || n === 0) {
    return n === 0 ? "0" : "-";
  }
  let value = Number(n);
  let decimals = value > 10 ? 1 : 2;

  if (!Number.isInteger(value) && value.toString().includes('.')) { //&& value.toString().split('.')[1].length > 2
    value = Number.parseFloat(value.toFixed(decimals));
  }

  if (value >= 1000 && (units === 'm' || units === 'metres')) {
    return (value / 1000).toFixed(1) + " km";
  }
  return units ? `${value} ${units}` : value;
}

/**
 * Given a match date in UTC format, returns the match date in the local user's format.
 * If the given date string is invalid, returns the original string.
 * @param {string} matchDate - the match date in UTC format (DD/MM/YYYY HH:mm)
 * @param {string} format - the desired format string (e.g. "DD/MM/YYYY HH:mm")
 * @returns {string} the match date in the local user's format, or the original string if the given date string is invalid.
 */
export function getLocalMatchDate(matchDate, format = "DD/MM/YYYY") {
  if (!matchDate) return "";

  // 1) "DD/MM/YYYY HH:mm" as UTC (strict)
  let m = moment.utc(matchDate, "DD/MM/YYYY HH:mm", true);

  // 2) "DD/MM/YYYY" as UTC (strict)
  if (!m.isValid()) m = moment.utc(matchDate, "DD/MM/YYYY", true);

  // 3) "DD/MM" as UTC (strict) -> assume current year, 00:00 UTC
  if (!m.isValid()) {
    const y = moment().utc().format("YYYY");
    m = moment.utc(`${matchDate}/${y}`, "DD/MM/YYYY", true);
  };

  // 4) ISO/RFC with offset (keeps zone)
  if (!m.isValid()) m = moment.parseZone(matchDate);

  // If parsed, convert to local and format
  if (m.isValid()) return m.local().format(format);

  // 5) Last resort: loose parse + format
  const loose = moment(matchDate);
  return loose.isValid() ? loose.format(format) : matchDate;
}

/**
 * Find the first match of a player that has the specific Arrigo data (PassesUnderPressure, LBP, OnBallPressure or BallReceipts)
 * To display as default match.
 * 
 * @param {Array} filteredGames List of the macthes with filters applied (if any).
 * @param {Array} events List of the Arrigo data.
 * @returns The ID of the first match with data.
 */
export function findFirstMatchWithDataForArrigoMaps(filteredGames, events) {
  let stop = false;
  let index = 0;
  let resultId = filteredGames[0].gameId;
  while (!stop && index < filteredGames.length) {
    const currentGame = filteredGames[index];
    const foundMatch = events.some(event => event.gameId === currentGame.gameId);
    if (foundMatch) {
      stop = true;
      resultId = currentGame.gameId;
    }
    else {
      index++;
    }
  }
  return resultId;
}

/**
 * Display the Logo for Insights rankings in the Global page
 * 
 * @param {Boolean} darkMode  determine if the theme of the application is in dark mode or not.
 * @returns the path of the logo
 */
export function getDriblabLogo(darkMode) {
  let image = new Image();
  image.src = require(darkMode ? "@/assets/img/driblab_white.png" : "@/assets/img/driblab_color.png");
  return image.src;
}

/**
 * Wraps the download logic for the Insights tab in the Globals Page.
 * 
 * @param {*} context Insights component (Player, Team or League).
 * @param {String} type Name of the component for the title of the PNG (Player, Team or League).
 */
export async function downloadInsightsRankingsAsPNG(context, type) {
  context.loadingPNG = true;

  try {
    const node = context.$refs.statsRef.cloneNode(true);
    const absoluteLogo = node.querySelector('#driblab-logo');
    if (absoluteLogo) {
      absoluteLogo.remove();
    }

    const wrapper = document.createElement('div');
    wrapper.style.width = '1880px';
    wrapper.style.backgroundColor = context.darkMode ? '#1e1e1e' : '#ffffff';
    wrapper.style.padding = '20px';
    wrapper.style.display = 'flex';
    wrapper.style.flexDirection = 'column';
    wrapper.style.boxSizing = 'border-box';
    wrapper.appendChild(node);

    // Exclusive footer for the logo
    const footerLogoContainer = document.createElement('div');
    footerLogoContainer.style.display = 'flex';
    footerLogoContainer.style.justifyContent = 'flex-end';
    footerLogoContainer.style.alignItems = 'center';
    footerLogoContainer.style.marginTop = '24px';
    footerLogoContainer.style.paddingRight = '16px';

    const logoImg = document.createElement('img');
    logoImg.src = getDriblabLogo(context.darkMode);
    logoImg.style.width = '140px';

    footerLogoContainer.appendChild(logoImg);
    wrapper.appendChild(footerLogoContainer);

    context.$refs.tableCloneRef.innerHTML = '';
    context.$refs.tableCloneRef.appendChild(wrapper);

    const canvas = await html2canvas(wrapper, {
      useCORS: true,
      backgroundColor: null,
      scale: 1
    });

    const parent = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    d3.select(parent).append('image').attr('href', canvas.toDataURL("image/png")).attr('width', canvas.width);

    parent.setAttribute('width', canvas.width);
    parent.setAttribute('height', canvas.height);

    context.$refs.tableCloneRef.innerHTML = '';
    downloadPng(parent, `Driblab_${type}_Insights_${moment().format('DD/MM/YYYY_HH:mm')}.png`);
  }
  catch (e) {
    console.error(e);
  }
  finally {
    context.loadingPNG = false;
  }
}

/**
  * Format the stats to a correct numeric value by removing '%' and ':'.
  * @param {string|number} val - The raw stat value from the table (e.g., "48.9%", "25:02").
  * @param {number|string} numericValue - Backup percentage value used if the stat is a time string.
  * @returns {number} The clean formatted numeric value, defaulting to 0.
*/
export function parseStatValue(val, numericValue) {
  if (typeof val === 'string') {
    if (val.includes(':')) {
      return Number(numericValue) || 0;
    }
    return Number(val.replace('%', '').trim()) || 0;
  }
  return Number(val) || 0;
}

export function imageUrlToBase64(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = (err) => reject(err);
    img.src = url + '?t=' + new Date().getTime();
  });
}

export function coordToArea(x,y,areas) {
  return _.find(areas,(a) => {
    return (a.x1 <= x && a.x2 >= x) && (a.y1 <= y && a.y2 >= y)
  })
}

/**
 * Obtain the pitch zone based on the coordinates. Useful for obtaining heat zones.
 * @param {*} x x coordinate.
 * @param {*} y  y coordinate
 * @returns the corresponding zone.
 */
export function pressureCoordToAreaName(x, y) {
  // Opponent area
  if (x >= 94.2 && y >= 36.8 && y <= 63.2) return 'goalArea';
  if (x >= 83 && y >= 21.1 && y < 36.8) return 'boxRight';
  if (x >= 83 && x < 94.2 && y >= 36.8 && y <= 63.2) return 'boxCenter';
  if (x >= 83 && y > 63.2 && y <= 78.9) return 'boxLeft';
  if (x >= 83 && y < 21.1) return 'outBoxDeepRight';
  if (x >= 83 && y > 78.9) return 'outBoxDeepLeft';

  if (x >= 65 && x < 83 && y < 21.1) return 'outBoxRight';
  if (x >= 65 && x < 83 && y >= 21.1 && y <= 35.5) return 'outBoxCentreRight';
  if (x >= 65 && x < 83 && y > 35.5 && y <= 64.4) return 'outBoxCentre';
  if (x >= 65 && x < 83 && y > 64.4 && y <= 78.9) return 'outBoxCentreLeft';
  if (x >= 65 && x < 83 && y > 78.9) return 'outBoxLeft';

  // Midfield
  if (x >= 35 && x < 65 && y > 78.8) return 'midfieldLeft35';
  if (x >= 35 && x < 65 && y >= 64.5 && y <= 78.9) return 'midfieldCentreLeft35';
  if (x >= 35 && x < 65 && y >= 35.6 && y <= 64.4) return 'midfieldCentre35';
  if (x >= 35 && x < 65 && y >= 21.1 && y <= 35.5) return 'midfieldCentreRight35';
  if (x >= 35 && x < 65 && y < 21.1) return 'midfieldRight35';

  // Own area
  if (x <= 5.8 && y >= 36.8 && y <= 63.2) return 'ownGoalArea';
  if (x <= 17 && y > 63.2 && y < 78.9) return 'ownBoxLeft';
  if (x > 5.8 && x <= 17 && y >= 36.8 && y < 63.2) return 'ownBoxCenter';
  if (x <= 17 && y >= 21.1 && y < 36.8) return 'ownBoxRight';

  if (x <= 17 && y < 21.1) return 'ownFieldOutOfBoxDeepRight';
  if (x <= 17 && y > 78.9) return 'ownFieldOutOfBoxDeepLeft';

  if (x > 17 && x < 35 && y > 78.9) return 'ownFieldOutOfBoxLeft';
  if (x > 17 && x < 35 && y >= 64.5 && y <= 78.9) return 'ownFieldOutOfBoxCentreLeft';
  if (x > 17 && x < 35 && y >= 35.6 && y <= 64.6) return 'ownFieldOutOfBoxCentre';
  if (x > 17 && x < 35 && y >= 21.1 && y <= 35.5) return 'ownFieldOutOfBoxCentreRight';
  if (x > 17 && x < 35 && y < 21.1) return 'ownFieldOutOfBoxRight';

  return null;
}
/** 
 * Returns the PDF font for a given language, falling back to CJK/Arabic fonts when needed.
 * @param {string} lang - Language code ('jp', 'ko', 'cn', 'sa').
 * @param {string} [defaultFont='NeueMontreal'] - Fallback font.
 * @returns {string} Font name for pdfMake.
 */
export function getPDFFontFamily(lang, defaultFont = 'NeueMontreal') {
  switch (lang) {
    case 'jp':
      return 'NotoSansJP';
    case 'ko':
      return 'NotoSansKR';
    case 'cn':
      return 'NotoSansSC';
    case 'sa':
      return 'NotoSansArabic';
    case 'ru':
      return 'NotoSansRuss';
    default:
      return defaultFont;
  }
}

/**
 * Obtain the are based on a selected confederation (Used in the Global Page).
 * 
 * @param {*} confederation said confederation.
 * @returns the area of the given confederation.
 */
export function getConfederationAreas(confederation) {
  const areaMap = {
    'uefa': [0, 1, 2],
    'conmebol': [3, 4],
    'concacaf': [5],
    'caf': [7],
    'afc': [6]
  };
  return areaMap[confederation] || [];
}

/**
 * Mapping based on continentId (not area like leagues).
 * UEFA: continentId 0.
 * CONMEBOL: continentId 1.
 * CONCACAF: continentId 2.
 * AFC: continentId 3.
 * CAF: continentId 4.
 * 
 * @param {*} confederation said confederation.
 * @returns the continentId of the given confederation.
 */
export function getConfederationContinentIds(confederation) {
  const continentIdMap = {
    'uefa': [0],
    'conmebol': [1],
    'concacaf': [2],
    'afc': [3],
    'caf': [4]
  };
  return continentIdMap[confederation] || [];
}

/**
 * Obtain the id of the top 5 biggest leagues (Used in the Global Page).
 * @param {Array} leagues
 * @returns the ids of the top 5 biggets leagues.
 */
export function getTop5LeagueIds(leagues) {
  const top5LeagueLabels = [
    'Premier League',
    'La Liga',
    'Bundesliga',
    'Ligue 1',
    'Serie A'
  ];
  if (!leagues || leagues.length === 0) {
    return [];
  }
  return leagues.filter(league => top5LeagueLabels.includes(league.label)).map(league => league.id);
}
